/* Auto-scrolling looping strips. Added by Oleg @ IAKOE.
   ONE module, two web components, zero imports:

     <ts-marquee>          the feature/logo ticker band (sections/ts-marquee)
     <ts-gallery-marquee>  the image strip on the contact page (sections/ts-image-gallery)

   Both share the private TsMarqueeBase below: measure one group, clone it until the
   track always fills the viewport, then translate the track by exactly one group width
   via the Web Animations API at a constant px/sec, forever. Pause on hover. Differences
   are expressed as subclass getters/hooks, same shape as ts-anchor-nav.js.

   Why one file: the base used to be imported relatively by two consumer files. A
   relative import carries no `?v=` hash, so Shopify's asset versioning never reaches it
   and browsers keep the first copy they ever cached — the first future edit to the base
   would have shipped a stale engine against fresh consumers and broken both strips for
   returning visitors (exactly how the anchor-nav highlighter blew up with "lockTo is
   not a function"). Same file = no imports, nothing to go stale, one request.

   Driven by the WAA, NOT a CSS @keyframes: iOS Safari ignores @keyframes that contain
   var(), while track.animate() runs identically there. The track holds >=2 identical
   groups, so shifting by exactly one group width loops seamlessly. */

/* ---------- shared engine (module-private) ---------- */

class TsMarqueeBase extends HTMLElement {
  /* --- overridable config --- */
  get trackSelector() {
    return '[data-mq-track]';
  }
  get groupSelector() {
    return '[data-mq-group]';
  }
  get defaultSpeed() {
    return 60; // px per second
  }
  get reversed() {
    return false;
  }
  /** Return true to leave the strip static (an opt-in reduced-motion pause, a static
      desktop row). Checked on every update, so it may depend on the viewport. */
  paused() {
    return false;
  }
  /** Runs once, after `update` is bound and before the first measure. */
  prepare() {}

  /* --- shared engine --- */
  connectedCallback() {
    this.track = this.querySelector(this.trackSelector);
    this.base = this.querySelector(this.groupSelector);
    if (!this.track || !this.base) return;

    this.update = this.update.bind(this);
    this.prepare();

    this.ro = new ResizeObserver(this.update);
    this.ro.observe(this);
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.update);
    }

    // Pause on hover (desktop); a no-op on touch devices.
    this.addEventListener('mouseenter', () => this.anim && this.anim.pause());
    this.addEventListener('mouseleave', () => this.anim && this.anim.play());

    this.update();
  }

  disconnectedCallback() {
    if (this.ro) this.ro.disconnect();
    if (this.anim) this.anim.cancel();
  }

  update() {
    // A paused strip stands still: drop a running animation (the breakpoint may have
    // just changed) and forget the width, so the next unpaused update rebuilds it.
    if (this.paused()) {
      if (this.anim) this.anim.cancel();
      this.anim = null;
      this._w = 0;
      return;
    }

    const groupWidth = this.base.offsetWidth;
    if (!groupWidth) return;

    // Clone the group until the track is wide enough to always fill the viewport
    // (so the one-group loop never shows an empty gap).
    const needed = Math.max(2, Math.ceil(this.offsetWidth / groupWidth) + 1);
    let have = this.track.querySelectorAll(this.groupSelector).length;
    while (have < needed) {
      const clone = this.base.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      this.track.appendChild(clone);
      have += 1;
    }

    // Rebuild the animation only when the shift distance actually changes.
    if (groupWidth === this._w && this.anim) return;
    this._w = groupWidth;
    if (this.anim) this.anim.cancel();

    const speed = parseFloat(this.dataset.speed) || this.defaultSpeed;
    this.anim = this.track.animate(
      [{ transform: 'translateX(0)' }, { transform: `translateX(${-groupWidth}px)` }],
      {
        duration: (groupWidth / speed) * 1000,
        iterations: Infinity,
        easing: 'linear',
        direction: this.reversed ? 'reverse' : 'normal',
      }
    );
  }
}

/* ---------- <ts-marquee>: the ticker band ----------
   Marquee-specific: the group/track classes, the 60 px/s default, the right-to-left
   direction (ts-mq--right), the CSS-fallback kill, and the reduced-motion opt-in.
   By default the ticker keeps running under prefers-reduced-motion (brand decision);
   the theme setting "Pause the marquee on Reduce Motion" (data-respect-reduced-motion)
   opts in. */

if (!customElements.get('ts-marquee')) {
  customElements.define(
    'ts-marquee',
    class TsMarquee extends TsMarqueeBase {
      get trackSelector() {
        return '.ts-mq__track';
      }
      get groupSelector() {
        return '.ts-mq__group';
      }
      get defaultSpeed() {
        return 60;
      }
      get reversed() {
        return this.classList.contains('ts-mq--right');
      }

      prepare() {
        // Kill the CSS fallback animation so it can't double-run on browsers that
        // DO honour it (the WAA animation is the single source).
        this.track.style.animation = 'none';
        // A logo has no width until it loads, so the static row's fit check (and the
        // group width) must run again once each image is in.
        this.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', this.update, { once: true });
        });
      }

      paused() {
        // "Static row on desktop" (data-static-desktop): still at 1024px+ AS LONG AS the
        // items fit, the CSS (.ts-mq--static-desktop) spreads the one real group across
        // the page and hides the clones. When they do not fit (a narrow desktop, the
        // editor preview) the class comes off and the strip scrolls like any marquee.
        if (this.dataset.staticDesktop === 'true') {
          const still = window.matchMedia('(min-width: 1024px)').matches && this.itemsFit();
          if (this.classList.contains('ts-mq--static-desktop') !== still) {
            this.classList.toggle('ts-mq--static-desktop', still);
          }
          if (still) return true;
        }
        return (
          this.dataset.respectReducedMotion === 'true' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        );
      }

      /* Natural width of one group (its items and dividers) against the width the static
         row may use: the whole strip beside a left header, else the strip minus the page
         gutters. The measure in static mode is smaller than in scrolling mode (the outer
         items drop their side padding there), which keeps it from flipping back and forth
         at the edge. */
      itemsFit() {
        let natural = 0;
        this.base.querySelectorAll('.ts-mq__item, .ts-mq__divider').forEach((el) => {
          natural += el.getBoundingClientRect().width;
          // A logo that has not loaded yet lays out at 0 wide: count the width it WILL
          // take, from its width/height attributes and the CSS caps.
          el.querySelectorAll('img').forEach((img) => {
            if (img.complete && img.naturalWidth) return;
            const w = parseFloat(img.getAttribute('width'));
            const h = parseFloat(img.getAttribute('height'));
            if (!w || !h) return;
            const cs = getComputedStyle(img);
            const height = Math.min(h, parseFloat(cs.maxHeight) || h);
            natural += Math.min((w / h) * height, parseFloat(cs.maxWidth) || Infinity);
          });
        });
        let available = this.clientWidth;
        if (!this.closest('.ts-mq-wrap--header-left')) {
          const pad = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
          const gutter = pad || parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ia-page-padding-x')) || 0;
          available -= gutter * 2;
        }
        return natural <= available + 1;
      }
    }
  );
}

/* ---------- <ts-gallery-marquee>: the image strip ----------
   Gallery-specific: the group/track classes, the slower 30 px/s default, and
   re-measuring once the images have their real dimensions (offsetWidth needs them). */

if (!customElements.get('ts-gallery-marquee')) {
  customElements.define(
    'ts-gallery-marquee',
    class TsGalleryMarquee extends TsMarqueeBase {
      get trackSelector() {
        return '.ts-gallery__track';
      }
      get groupSelector() {
        return '.ts-gallery__group';
      }
      get defaultSpeed() {
        return 30;
      }

      prepare() {
        // A not-yet-loaded image has no width, so the first measure would be short.
        this.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', this.update, { once: true });
        });
      }
    }
  );
}
